/**
 * CORE-G-14 shared slow-load primitive.
 *
 * Chans wanted the loading states across Management tabs to be honest
 * about the Google Ads API being slow — a plain spinner + one-line label
 * ("Pulling {tenant}'s recent search terms and negatives.") reads as
 * "why is nothing happening" when Google actually takes 20-60 seconds
 * to answer.
 *
 * How it renders
 *
 *  - First N milliseconds (default 4000): plain spinner + label. The
 *    operator's mental model is "loaded fine, just a moment."
 *  - After N ms: a second card appears BELOW with (a) an honest note
 *    that Google Ads is slow and this is normal, and (b) a determinate
 *    progress bar that ramps from 0 to ~90 % over half a minute and
 *    then holds. It never claims to be finished until the caller
 *    actually finishes. The ramp is log-scale so early motion is
 *    faster (matches how operators expect real progress to feel).
 *
 * Why not indeterminate
 *
 *   An indeterminate "moving stripe" bar reads as "we don't know if
 *   this is doing anything." The ramp we render says "elapsed time is
 *   accumulating," which is at least a truthful signal.
 *
 * Copy note
 *
 *   The slow-panel copy sits in one place for Chans's tone-review — a
 *   single red-line here changes every Management tab at once. Do not
 *   inline the wording at consumer sites.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

export interface AdsSlowLoadProps {
  /**
   * The "what is loading" line, minus punctuation. Example:
   * `"Reading flagged search terms"`. The primitive appends the tenant
   * subject (if any) and a trailing ellipsis.
   */
  label: string;
  /**
   * Optional subject — usually a tenant id — the primitive renders as
   * ` for ${tenantId}` after the label. When omitted, only the label
   * is shown.
   */
  tenantId?: string | null;
  /**
   * How long to wait before showing the "Google is slow, hang tight"
   * panel. Default 4000 ms. Set to 0 to always show the panel from
   * mount (used in the tests that guard the panel's copy).
   */
  showSlowAfterMs?: number;
  /** Optional class for the outer wrapper. */
  className?: string;
}

/**
 * Chans's copy for the slow panel. Kept as a module constant so a
 * single red-line touches every adopter at once (§7 G-1 tone rule).
 * Draft only — Chans has final say on the exact wording.
 */
const SLOW_LOAD_COPY =
  "Google Ads is taking its time — this is normal, not a bug. Their API sets the pace, we just wait for it. Anywhere from a few seconds to a minute.";

/**
 * Log-scale ramp: reaches ~63 % at 15 s, ~86 % at 30 s, ~95 % at 45 s,
 * and asymptotes at 90 % (never claims completion). Feels like early
 * activity is faster than late activity, which matches operator
 * intuition better than a linear ramp.
 */
function fauxProgress(elapsedMs: number): number {
  const cap = 90;
  const tau = 15_000; // 15-second time constant
  return Math.min(cap, cap * (1 - Math.exp(-elapsedMs / tau)));
}

export function AdsSlowLoad({
  label,
  tenantId,
  showSlowAfterMs = 4000,
  className,
}: AdsSlowLoadProps) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const id = window.setInterval(() => {
      setElapsedMs(performance.now() - start);
    }, 250);
    return () => window.clearInterval(id);
  }, []);

  const showSlow = elapsedMs >= showSlowAfterMs;
  const progressValue = fauxProgress(elapsedMs);

  return (
    <div
      className={cn('space-y-3', className)}
      data-testid="ads-slow-load"
      data-slow-visible={showSlow ? 'true' : 'false'}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span>
          {label}
          {tenantId ? ` for ${tenantId}` : ''}
          &hellip;
        </span>
      </div>
      {showSlow && (
        <div
          className="rounded-md border border-border bg-muted/30 p-3"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-foreground">{SLOW_LOAD_COPY}</p>
          <Progress value={progressValue} className="mt-2 h-1.5" />
        </div>
      )}
    </div>
  );
}

export default AdsSlowLoad;

// ---------------------------------------------------------------------------
// Adoption register — mirrors the shape of the G-5 / G-6 / G-8 registers so
// a reader can see which tabs have moved to the shared slow-load primitive.
// ---------------------------------------------------------------------------

export interface SlowLoadAdoption {
  tab: string;
  ticket: string;
  adopted: boolean;
  note?: string;
}

export const ADS_SLOW_LOAD_ADOPTED_BY: readonly SlowLoadAdoption[] = [
  {
    tab: 'Google Recommendations (M-02)',
    ticket: 'CORE-G-14',
    adopted: true,
    note: 'Top-level rec list load renders AdsSlowLoad.',
  },
  {
    tab: 'Negative Keyword Suggestions (M-03)',
    ticket: 'CORE-G-14',
    adopted: true,
    note: 'Top-level review call renders AdsSlowLoad.',
  },
  {
    tab: 'Campaign structure drill-down (M-11)',
    ticket: 'CORE-G-14',
    adopted: true,
    note:
      'Top-level campaigns list renders AdsSlowLoad. Nested ad-group / ads / keywords loads keep the plain inline spinner — those reads are fast enough that a full slow-load panel would be visually noisy.',
  },
  {
    tab: 'Ad Review (M-06)',
    ticket: 'CORE-G-14.a',
    adopted: false,
    note: 'AdReviewTab loading state adopts in a follow-up once the tab body itself is stable.',
  },
  {
    tab: 'Budget Reallocation (M-08)',
    ticket: 'CORE-G-14.b',
    adopted: false,
    note: 'BudgetReallocationTab has its own multi-query loading shape; adoption lands with M-08.b.',
  },
];
